// Honey Tycoon — a buy-low / sell-high trading sim with an apiary to tend.
//
// Visit world markets (each visit = a new day) and trade honey, supplies and
// crafted goods whose prices fluctuate daily. Run hives: TEND them each day (for
// a fee) to gather honey (max 10), plus beeswax (every 3rd tend) and propolis
// (every 7th tend); collect and sell it. Standard hives make 1 honey/tend; rare
// hives make it slowly (Manuka every 4 tends, Royal Jelly every 8). From Level 10
// random disasters can strike a hive — it stops producing and must be healed (at
// a per-day cost) over several tends. The pantry has a capacity you grow by
// buying jars and storage. Grow the bank as high as you can.
//
// Prices derive deterministically from (market, good, day); disasters use live
// randomness but are baked into the saved state, so a reload reproduces prices
// and preserves whatever happened.

export const GOODS = [
  // 0–5 honeys
  { name: "Wildflower Honey", base: 22, kind: "honey", glyph: "🌼", color: "#F59E0B", unlock: 1 },
  { name: "Clover Honey", base: 34, kind: "honey", glyph: "🍀", color: "#EAB308", unlock: 1 },
  { name: "Orange Blossom", base: 52, kind: "honey", glyph: "🍊", color: "#FB923C", unlock: 1 },
  { name: "Buckwheat Honey", base: 80, kind: "honey", glyph: "🌾", color: "#92400E", unlock: 1 },
  { name: "Manuka Honey", base: 170, kind: "rare", glyph: "✨", color: "#DB2777", unlock: 1 },
  { name: "Royal Jelly", base: 250, kind: "rare", glyph: "👑", color: "#A855F7", unlock: 1 },
  // 6–7 supplies
  { name: "Beeswax", base: 28, kind: "supply", glyph: "🕯️", color: "#D97706", unlock: 1 },
  { name: "Propolis", base: 95, kind: "supply", glyph: "🧪", color: "#65A30D", unlock: 1 },
  // 8–23 crafted goods — one unlocks every 3 levels from Level 5
  { name: "Whipped Honey", base: 45, kind: "craft", glyph: "🍮", color: "#FBBF24", unlock: 5 },
  { name: "Honeycomb", base: 60, kind: "supply", glyph: "🧇", color: "#F59E0B", unlock: 1 }, // collected from hives (every 6 tends), sellable immediately
  { name: "Honey Butter", base: 50, kind: "craft", glyph: "🧈", color: "#FCD34D", unlock: 11 },
  { name: "Hot Honey", base: 58, kind: "craft", glyph: "🌶️", color: "#EF4444", unlock: 14 },
  { name: "Beeswax Candle", base: 95, kind: "craft", glyph: "🕯️", color: "#F59E0B", unlock: 17 },
  { name: "Beeswax Balm", base: 72, kind: "craft", glyph: "💛", color: "#FBBF24", unlock: 20 },
  { name: "Lotion Bars", base: 110, kind: "craft", glyph: "🧴", color: "#FBCFE8", unlock: 23 },
  { name: "Soap", base: 80, kind: "craft", glyph: "🧼", color: "#93C5FD", unlock: 26 },
  { name: "Modeling Wax", base: 70, kind: "craft", glyph: "🎨", color: "#FCA5A5", unlock: 29 },
  { name: "Incense", base: 88, kind: "craft", glyph: "🪔", color: "#C084FC", unlock: 32 },
  { name: "Propolis Tincture", base: 210, kind: "craft", glyph: "🧪", color: "#84CC16", unlock: 35 },
  { name: "Propolis Salve", base: 170, kind: "craft", glyph: "🫙", color: "#4D7C0F", unlock: 38 },
  { name: "Propolis Mouthwash", base: 160, kind: "craft", glyph: "🦷", color: "#34D399", unlock: 41 },
  { name: "Propolis Cream", base: 220, kind: "craft", glyph: "🥛", color: "#A3E635", unlock: 44 },
  { name: "Propolis Face Mask", base: 260, kind: "craft", glyph: "🎭", color: "#65A30D", unlock: 47 },
  { name: "Propolis Shampoo", base: 190, kind: "craft", glyph: "🧴", color: "#22C55E", unlock: 50 },
  // 24–29 oven-baked goods (need a stove/oven, from Level 10)
  { name: "Honey Walnut Bundt Cake", base: 150, kind: "oven", glyph: "🍰", color: "#F9A8D4", unlock: 10 },
  { name: "Salt Water Taffy", base: 95, kind: "oven", glyph: "🍬", color: "#F472B6", unlock: 10 },
  { name: "Vanilla Honey Snaps", base: 90, kind: "oven", glyph: "🍪", color: "#B45309", unlock: 10 },
  { name: "Orange Poppyseed Muffins", base: 115, kind: "oven", glyph: "🧁", color: "#D97706", unlock: 10 },
  { name: "Lavender Lemonade", base: 80, kind: "oven", glyph: "🍋", color: "#C4B5FD", unlock: 10 },
  { name: "Barrel Crafted Mead", base: 240, kind: "oven", glyph: "🍺", color: "#F59E0B", unlock: 10 },
  // 30 crafted (replaces the old Honeycomb craft slot)
  { name: "Honey Sticks", base: 55, kind: "craft", glyph: "🍡", color: "#F59E0B", unlock: 8 },
];

export const BEESWAX = 6;
export const PROPOLIS = 7;
export const HONEYCOMB = 9;

// Hand-crafted goods (goods 8–23). Level matches each good's market unlock.
// Each crafted good blends a few different honeys / wax / propolis (goods 0–7).
export const RECIPES = [
  { out: 8, level: 5, ins: [{ good: 0, qty: 2 }, { good: 1, qty: 1 }] }, // Whipped Honey: Wildflower + Clover
  { out: 30, level: 8, ins: [{ good: 0, qty: 2 }, { good: 6, qty: 1 }] }, // Honey Sticks: Wildflower + Beeswax
  { out: 10, level: 11, ins: [{ good: 1, qty: 2 }, { good: 6, qty: 1 }] }, // Honey Butter: Clover + Beeswax
  { out: 11, level: 14, ins: [{ good: 2, qty: 1 }, { good: 3, qty: 1 }] }, // Hot Honey: Orange Blossom + Buckwheat
  { out: 12, level: 17, ins: [{ good: 6, qty: 2 }, { good: 7, qty: 1 }] }, // Beeswax Candle: Beeswax + Propolis
  { out: 13, level: 20, ins: [{ good: 6, qty: 2 }, { good: 1, qty: 1 }] }, // Beeswax Balm: Beeswax + Clover
  { out: 14, level: 23, ins: [{ good: 6, qty: 2 }, { good: 2, qty: 1 }] }, // Lotion Bars: Beeswax + Orange Blossom
  { out: 15, level: 26, ins: [{ good: 6, qty: 2 }, { good: 0, qty: 1 }] }, // Soap: Beeswax + Wildflower
  { out: 16, level: 29, ins: [{ good: 6, qty: 3 }] }, // Modeling Wax: Beeswax
  { out: 17, level: 32, ins: [{ good: 6, qty: 1 }, { good: 7, qty: 1 }, { good: 3, qty: 1 }] }, // Incense: Beeswax + Propolis + Buckwheat
  { out: 18, level: 35, ins: [{ good: 7, qty: 2 }, { good: 0, qty: 1 }] }, // Propolis Tincture: Propolis + Wildflower
  { out: 19, level: 38, ins: [{ good: 7, qty: 2 }, { good: 6, qty: 1 }] }, // Propolis Salve: Propolis + Beeswax
  { out: 20, level: 41, ins: [{ good: 7, qty: 1 }, { good: 4, qty: 1 }] }, // Propolis Mouthwash: Propolis + Manuka
  { out: 21, level: 44, ins: [{ good: 7, qty: 2 }, { good: 6, qty: 1 }, { good: 1, qty: 1 }] }, // Propolis Cream: Propolis + Beeswax + Clover
  { out: 22, level: 47, ins: [{ good: 7, qty: 2 }, { good: 4, qty: 1 }] }, // Propolis Face Mask: Propolis + Manuka
  { out: 23, level: 50, ins: [{ good: 7, qty: 2 }, { good: 2, qty: 1 }] }, // Propolis Shampoo: Propolis + Orange Blossom
];

// Oven-baked goods (goods 24–29). Need a stove/oven; each takes `bake` days.
// Each blends a couple of different honeys (goods 0–3: Wildflower/Clover/Orange
// Blossom/Buckwheat) so the whole honey range has a use, not just Wildflower.
export const OVEN_ITEMS = [
  { out: 24, ins: [{ good: 3, qty: 1 }, { good: 0, qty: 1 }], bake: 2, level: 10 }, // Honey Walnut Bundt Cake: Buckwheat + Wildflower
  { out: 25, ins: [{ good: 1, qty: 2 }], bake: 1, level: 10 }, // Salt Water Taffy: Clover
  { out: 26, ins: [{ good: 0, qty: 1 }, { good: 1, qty: 1 }], bake: 1, level: 10 }, // Vanilla Honey Snaps: Wildflower + Clover
  { out: 27, ins: [{ good: 2, qty: 1 }, { good: 0, qty: 1 }], bake: 2, level: 10 }, // Orange Poppyseed Muffins: Orange Blossom + Wildflower
  { out: 28, ins: [{ good: 1, qty: 1 }, { good: 0, qty: 1 }], bake: 1, level: 10 }, // Lavender Lemonade: Clover + Wildflower
  { out: 29, ins: [{ good: 3, qty: 1 }, { good: 2, qty: 1 }, { good: 0, qty: 1 }], bake: 3, level: 10 }, // Barrel Crafted Mead: Buckwheat + Orange Blossom + Wildflower
];

// Oven economy.
export const OVEN_COST = 150000;
export const OVEN_MAX_SLOTS = 8;
export const OVEN_MAX_BATCH = 10;
export const OVEN_BATCH_COST = 10000; // per +1 batch, up to 10
// You may own one oven from Level 10, plus one more every 5 levels after (15, 20…).
export const ovenSlotsAllowed = (level) => (level < 10 ? 0 : Math.min(OVEN_MAX_SLOTS, Math.floor(level / 5) - 1));
// Level needed to own `count` ovens (1→10, 2→15, 3→20, …).
export const ovenSlotUnlockLevel = (count) => (count + 1) * 5;
// Cost to buy the slot that takes you from `slots` ovens to `slots`+1.
const OVEN_SLOT_COSTS = [25000, 45000, 80000, 130000, 200000, 300000, 450000];
export const ovenSlotUpgradeCost = (slots) => OVEN_SLOT_COSTS[slots - 1] || 0;

// Auto-tend & collect subscription (from Level 7): each purchased day auto-tends
// (free) and collects every healthy hive when a day passes.
export const AUTO_LEVEL = 7;
export const AUTO_PLANS = [
  { days: 30, cost: 30000 },
  { days: 60, cost: 50000 },
  { days: 90, cost: 75000 },
];

// First 4 markets open at level 1; then one more market every 5 levels.
export const MARKETS = [
  { name: "Hometown Market", unlock: 1 },
  { name: "Riverside Bazaar", unlock: 1 },
  { name: "Mountain Village", unlock: 1 },
  { name: "Coastal Port", unlock: 1 },
  { name: "Golden Valley", unlock: 5 },
  { name: "Savanna Post", unlock: 10 },
  { name: "Alpine Market", unlock: 15 },
  { name: "Desert Oasis", unlock: 20 },
  { name: "Rainforest Exchange", unlock: 25 },
  { name: "Tundra Outpost", unlock: 30 },
  { name: "Island Marketplace", unlock: 35 },
  { name: "Grand Capital", unlock: 40 },
  { name: "Silk Road Hub", unlock: 45 },
  { name: "Arctic Depot", unlock: 50 },
  { name: "Royal Court", unlock: 55 },
];

const HEALTH_DECAY = 12;
const HONEY_MAX = 10;
const MAX_HIVES = 24;
export const BULK_FEE = 0.05; // 5% surcharge/handling fee for "all" actions

const DISASTER_LEVEL = 10; // disasters begin at this level
const DISASTER_BASE = 0.01; // base daily chance for a well-tended hive
const DISASTER_PER_DAY = 0.02; // +2% chance for each day left unattended
const DISASTER_MAX = 0.25; // cap on daily disaster chance
// Queen resistance is applied as resist², so a 1.3 queen roughly halves the risk
// (a plain divide barely moved the needle).
const DISASTER_RESIST_POWER = 2;
// Grace period after a hive recovers — a just-cured colony can't be struck again
// immediately (recovering hives were previously re-struck right away because they
// can't be tended while diseased, which made `unattended` snowball).
const DISASTER_IMMUNE_DAYS = 5;
const SPREAD_CHANCE = 0.3; // chance an ignored disaster spreads to another hive
export const INSTANT_FEE = 1.35; // instant-resolve = remaining cost × this
// Insured hives pay 90% less to treat/resolve a disaster.
export const INSURED_RECOVERY_DISCOUNT = 0.9;
// Insurance pays back 10× everything sunk into the hive (purchase + upgrades).
export const INSURANCE_PAYOUT_MULT = 10;
// Premium is a share of that payout (not of the hive price), so cover scales with
// what it actually pays out. 0.3 → a $500k hive costs $1.5M to insure for $5M.
export const INSURANCE_PREMIUM_RATE = 0.3;

// Each disaster: how many days it freezes production ([min,max], picked at
// random), and a short story. Daily remediation cost is the hive's healCost.
export const DISASTERS = [
  { name: "Varroa Mite", min: 6, max: 12, story: "A rainstorm overnight loosed a bloom of varroa mites onto your hive; they're latching onto brood and bees alike." },
  { name: "Tracheal Mite", min: 8, max: 15, story: "Microscopic tracheal mites have crept into your bees' breathing tubes, leaving the colony wheezing and weak." },
  { name: "Tropilaelaps Mite", min: 12, max: 20, story: "Aggressive tropilaelaps mites have overrun the brood nest, breeding faster than your bees can cope." },
  { name: "Bee Louse", min: 1, max: 3, story: "Tiny bee lice are riding your workers and stealing food right from their mouths." },
  { name: "American Foulbrood Disease", min: 15, max: 22, story: "The dreaded American foulbrood has taken hold — a ropey, foul-smelling rot is killing the capped brood." },
  { name: "European Foulbrood", min: 8, max: 12, story: "European foulbrood has curdled through the larvae, twisting and yellowing the young brood." },
  { name: "Sacbrood", min: 2, max: 5, story: "Sacbrood virus has left fluid-filled larvae drooping in their cells like little sacs." },
  { name: "Chalkbrood", min: 3, max: 7, story: "A cold, damp snap brought chalkbrood fungus — larvae are mummifying into chalky white lumps." },
  { name: "Stonebrood", min: 2, max: 5, story: "Stonebrood fungus has hardened larvae to stone and dusted the comb with green spores." },
  { name: "Nosema Disease", min: 5, max: 11, story: "Nosema has infected your bees' guts; dysentery streaks the hive front and the foragers are dwindling." },
  { name: "Acarine Disease", min: 6, max: 10, story: "Acarine disease is clogging your bees' tracheae — crawling, flightless workers litter the ground." },
  { name: "Chronic Bee Paralysis Virus", min: 8, max: 14, story: "Chronic paralysis virus has your bees trembling, hairless and shiny, twitching at the entrance." },
  { name: "Small Hive Beetle", min: 4, max: 10, story: "Small hive beetles have slipped inside and are sliming the comb with their fermenting larvae." },
  { name: "Greater Wax Moth", min: 6, max: 10, story: "Greater wax moths have moved in, tunneling silken webs through your precious comb." },
  { name: "Lesser Wax Moth", min: 5, max: 9, story: "Lesser wax moths are chewing galleries across the frames and fraying the comb." },
  { name: "Ants", min: 5, max: 20, story: "A relentless column of ants has found the honey and is raiding your hive day and night." },
  { name: "Wasps & Yellow Jackets", min: 3, max: 30, story: "Wasps and yellow jackets are laying siege to the entrance, picking off guard bees one by one." },
  { name: "Robber Flies", min: 1, max: 4, story: "Robber flies are perched nearby, snatching your foragers out of the air mid-flight." },
  { name: "Dragonflies", min: 1, max: 4, story: "A hunting pack of dragonflies is patrolling the apiary and thinning your foragers." },
  { name: "Praying Mantises", min: 1, max: 4, story: "A praying mantis has staked out the landing board, ambushing returning bees." },
  { name: "Ambush Bugs", min: 1, max: 9, story: "Ambush bugs are lurking in the blossoms, seizing your foragers as they land." },
  { name: "Termites", min: 1, max: 4, story: "Termites have found the hive stand and are quietly eating away at the woodenware." },
  { name: "Spiders", min: 1, max: 4, story: "Spiders have strung webs around the entrance, trapping bees as they come and go." },
  { name: "Earwigs", min: 1, max: 9, story: "Earwigs have crept under the lid and are nesting in the warm inner cover." },
  { name: "Cockroaches", min: 1, max: 4, story: "Cockroaches have infested the hive floor, fouling comb and unsettling the colony." },
  { name: "Bears", min: 10, max: 12, story: "A black bear infiltrated your apiary and clawed the hive apart — the bees are angry and stressed." },
  { name: "Skunks", min: 8, max: 14, story: "A skunk has been scratching at the entrance each night, eating bees and agitating the colony." },
  { name: "Mice", min: 8, max: 12, story: "Mice have nested inside for warmth, shredding comb and soiling the hive." },
  { name: "Raccoons", min: 7, max: 15, story: "Raccoons have pried at the lid nightly, raiding stores and stressing the colony." },
  { name: "Birds", min: 3, max: 10, story: "Bee-eating birds have discovered your apiary and are picking off foragers on the wing." },
  { name: "Toads", min: 3, max: 8, story: "Toads have gathered beneath the entrance, gulping down bees as they land." },
  { name: "Lizards", min: 3, max: 8, story: "Lizards are basking by the landing board and snapping up your returning foragers." },
  { name: "Vandalism", min: 2, max: 20, story: "Vandals tipped and damaged your hive overnight, scattering frames and enraging the bees." },
  { name: "Theft of Equipment", min: 3, max: 15, story: "Thieves made off with hive equipment, leaving the colony exposed and struggling." },
  { name: "Pesticide Poisoning", min: 6, max: 20, story: "A neighbor's pesticide drift has poisoned your foragers — piles of dead bees ring the hive." },
  { name: "Colony Collapse Disorder", min: 3, max: 20, story: "Something triggered colony collapse — workers are vanishing, leaving queen and brood behind." },
  { name: "Starvation", min: 3, max: 20, story: "Stores ran dry during a dearth and the colony is starving on empty comb." },
  { name: "Robbing", min: 3, max: 9, story: "Stronger colonies have started robbing your hive; a frenzy of fighting bees clouds the entrance." },
  { name: "Swarming", min: 4, max: 10, story: "Your colony is preparing to swarm — half the workforce is about to abscond with the old queen." },
  { name: "Queen Failure", min: 5, max: 13, story: "The queen is failing — her laying has faltered and the colony is turning restless and queenless." },
  { name: "Extreme Weather — Winterkill", min: 3, max: 12, story: "An extreme cold snap has frozen the cluster; the survivors are shivering and few." },
  { name: "Extreme Weather — Summer Heat", min: 3, max: 12, story: "A brutal heat wave is melting comb and sending the colony bearding in exhaustion." },
  { name: "Extreme Weather — Flooding", min: 2, max: 4, story: "Floodwaters swept through the apiary, soaking the hive and drowning foragers." },
  { name: "Extreme Weather — Windstorms", min: 2, max: 4, story: "A windstorm toppled your hive and scattered the boxes across the yard." },
  { name: "Extreme Weather — Drought", min: 8, max: 20, story: "A long drought has withered every bloom for miles; there's no nectar and the colony is fading." },
  { name: "Fires", min: 8, max: 20, story: "Wildfire smoke and heat have swept the apiary, scorching forage and choking the hive." },
  { name: "Equipment Failure", min: 2, max: 5, story: "A cracked box and rotten frames have let the weather in — the hive's housing has failed." },
  { name: "Drone-Laying Queen", min: 2, max: 4, story: "Your queen has gone drone-laying, filling worker cells with useless drone brood." },
  { name: "Laying Workers", min: 3, max: 5, story: "With no queen pheromone, laying workers have taken over and the brood pattern is chaos." },
  { name: "Aggressive Temperament", min: 4, max: 10, story: "The colony has turned hot and defensive, making it dangerous to work and tend." },
  { name: "Absconding", min: 5, max: 20, story: "Fed up with conditions, the whole colony is threatening to abscond and leave the hive empty." },
];

const _disMap = new Map(DISASTERS.map((d) => [d.name, d]));
export const disasterStory = (name) => (_disMap.get(name) ? _disMap.get(name).story : "");
// Recovery pricing. Insured hives pay INSURED_RECOVERY_DISCOUNT less; the *Full
// variants give the undiscounted price so the UI can show it struck through.
const recoveryRate = (h) => (h && h.insured ? 1 - INSURED_RECOVERY_DISCOUNT : 1);
export const treatCostFull = (h) => Math.round(h.disDaily || 0);
export const treatCost = (h) => Math.round(treatCostFull(h) * recoveryRate(h));
export const instantCostFull = (h) => Math.round((h.disDaysLeft || 0) * (h.disDaily || 0) * INSTANT_FEE);
export const instantCost = (h) => Math.round(instantCostFull(h) * recoveryRate(h));

export const PANTRY_START = 200;
export const STORAGE_BASE = 4000; // cost of the first storage upgrade (60% cheaper than the old 10k)
export const STORAGE_ADD = 100; // +100 items per purchase
export const STORAGE_GROWTH = 1.25; // each purchase costs 25% more
// Cost of the next storage upgrade, rising 25% per prior purchase.
export const storageCost = (s) => Math.round((STORAGE_BASE * Math.pow(STORAGE_GROWTH, s.storageBuys || 0)) / 10) * 10;

// Purchasable hive types. `every` = tends per unit of honey; `tend` = cost to
// tend for a day; `heal` = cost per day to heal after a disaster.
export const HIVE_TYPES = [
  { good: 0, kind: "standard", cost: 10000, every: 1, tend: 100, heal: 20000 }, // Wildflower
  { good: 1, kind: "standard", cost: 22000, every: 1, tend: 100, heal: 20000 }, // Clover
  { good: 2, kind: "standard", cost: 45000, every: 1, tend: 100, heal: 20000 }, // Orange Blossom
  { good: 3, kind: "standard", cost: 75000, every: 1, tend: 100, heal: 20000 }, // Buckwheat
  { good: 4, kind: "special", cost: 100000, every: 4, tend: 1000, heal: 50000 }, // Manuka
  { good: 5, kind: "special", cost: 500000, every: 8, tend: 1500, heal: 75000 }, // Royal Jelly
];

export const shortName = (good) => GOODS[good].name.replace(" Honey", "");
const typeByGood = (good) => HIVE_TYPES.find((t) => t.good === good) || HIVE_TYPES[0];

function hash(a, b, c) {
  let h = (Math.imul(a | 0, 73856093) ^ Math.imul(b | 0, 19349663) ^ Math.imul(c | 0, 83492791)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 13), 0x297a2d39);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// ── Prices: a smooth per-good trend (layered waves) so prices drift day to day
// instead of being random noise — you can buy a dip and ride the rise. Prices are
// a deterministic function of (market, good, day, seed0), so history and the
// current day both reproduce exactly, and past days can be re-derived for charts.
const TWO_PI = Math.PI * 2;
function priceTrend(market, good, day, seed0) {
  const ph = (k) => hash(((market * 31 + good) | 0) ^ 0x51ed, seed0 | 0, (900 + k) | 0) * TWO_PI;
  return (
    0.55 * Math.sin(day * 0.4833 + ph(1)) + // ~13-day swing
    0.3 * Math.sin(day * 1.1424 + ph(2)) + // ~5.5-day swing
    0.15 * Math.sin(day * 2.3271 + ph(3)) // ~2.7-day ripple
  );
}

export function priceAt(market, good, day, seed0) {
  const g = GOODS[good];
  const rare = g.kind === "rare";
  const character = 0.85 + hash(market, good, 777) * 0.3; // some markets favour some goods
  const w = priceTrend(market, good, day, seed0); // −1..1 smooth trend
  const jitter = (hash(good, market, (day * 2 + 1) | 0) - 0.5) * 0.16; // small daily texture
  const mult = rare ? character * (1.65 + w * 0.45 + jitter * 0.6) : character * (1.0 + w * 0.55 + jitter);
  let p = g.base * Math.max(0.25, mult);
  const ev = hash(good, market, (day ^ 0x5bd1e995) | 0);
  if (ev > (rare ? 0.93 : 0.96)) p *= 1.9; // occasional shortage spike
  else if (ev < (rare ? 0.03 : 0.05)) p *= 0.5; // occasional glut
  p *= eventMult(market, good, day, seed0); // festivals / surpluses
  return Math.max(1, Math.round(p));
}

export function priceClass(market, good, day, seed0) {
  const p = priceAt(market, good, day, seed0);
  const base = GOODS[good].base;
  if (p <= base * 0.8) return "cheap";
  if (p >= base * 1.35) return "high";
  return "mid";
}

// Last `n` days of price (oldest → newest) for a sparkline.
export function priceHistory(market, good, day, seed0, n = 10) {
  const out = [];
  for (let k = n - 1; k >= 0; k--) out.push(priceAt(market, good, day - k, seed0));
  return out;
}

// ── Market events: telegraphed festivals (demand spike → sell high) and
// surpluses (glut → buy cheap) that hit one good-kind at one market for a few
// days. Deterministic from (day, seed0), so tomorrow's event can be previewed.
const EVENT_CHANCE = 0.33;
const EVENT_MIN_DAYS = 2;
const EVENT_MAX_DAYS = 4;
const EVENT_KINDS = ["honey", "rare", "supply", "craft", "oven"];
const KIND_LABEL = { honey: "Honey", rare: "Rare honey", supply: "Supplies", craft: "Crafted goods", oven: "Baked goods" };

function eventOnDay(D, seed0) {
  if (hash((D * 2654435761) | 0, seed0 | 0, 101) >= EVENT_CHANCE) return null;
  const market = Math.floor(hash(D | 0, seed0 | 0, 102) * MARKETS.length);
  const kind = EVENT_KINDS[Math.floor(hash(D | 0, seed0 | 0, 103) * EVENT_KINDS.length)];
  const boom = hash(D | 0, seed0 | 0, 104) > 0.42; // ~58% demand spikes
  const days = EVENT_MIN_DAYS + Math.floor(hash(D | 0, seed0 | 0, 105) * (EVENT_MAX_DAYS - EVENT_MIN_DAYS + 1));
  const r = hash(D | 0, seed0 | 0, 106);
  const mult = boom ? 1.5 + r * 0.7 : 0.55 - r * 0.15; // boom 1.5–2.2×, bust 0.4–0.55×
  return { startDay: D, days, market, kind, boom, mult: Math.round(mult * 100) / 100 };
}

function eventMult(market, good, day, seed0) {
  const kind = GOODS[good].kind;
  for (let D = day - EVENT_MAX_DAYS + 1; D <= day; D++) {
    const e = eventOnDay(D, seed0);
    if (e && e.market === market && e.kind === kind && day < e.startDay + e.days) return e.mult;
  }
  return 1;
}

// Events happening today (with days left) and the one starting tomorrow (preview).
export function marketEvents(day, seed0) {
  const active = [];
  for (let D = day - EVENT_MAX_DAYS + 1; D <= day; D++) {
    const e = eventOnDay(D, seed0);
    if (e && day < e.startDay + e.days) active.push({ ...e, daysLeft: e.startDay + e.days - day });
  }
  const up = eventOnDay(day + 1, seed0);
  return { active, upcoming: up ? [up] : [] };
}

export function eventLabel(e) {
  const k = KIND_LABEL[e.kind] || e.kind;
  const m = MARKETS[e.market].name;
  const pct = Math.round(Math.abs(e.mult - 1) * 100);
  return e.boom ? `🎉 ${k} in demand at ${m} — sells +${pct}%` : `📦 ${k} surplus at ${m} — prices −${pct}%`;
}

// Daily buy/sell quantity limits (tracked separately). Each day a market can move
// between QTY_MIN and a level-scaled ceiling of a good; a good that's in demand
// (high price → bigger sell limit) or in surplus (low price → bigger buy limit)
// sits near the top of that range in proportion to how strong the swing is, while
// ordinary goods sit in the low-to-mid range.
export const QTY_MIN = 200;
export const QTY_MAX = 5000; // absolute ceiling (reached at high level)
const QTY_MAX_START = 500; // ceiling at Level 1
const QTY_MAX_PER = 100; // +100 units…
const QTY_MAX_EVERY = 2; // …every 2 levels, until QTY_MAX
// The daily ceiling grows with level: 500 at Lv 1, +100 every 2 levels, up to 5000.
export const qtyMaxForLevel = (level) => Math.min(QTY_MAX, QTY_MAX_START + QTY_MAX_PER * Math.floor((Math.max(1, level | 0) - 1) / QTY_MAX_EVERY));
// signal ∈ [0,1]: 0 = ordinary (low-to-mid), 1 = strongly in demand/surplus (top).
function marketQty(market, good, level, daySeed, saltA, saltB, signal) {
  const max = qtyMaxForLevel(level);
  const r = hash((market ^ saltA) | 0, good, (daySeed ^ saltB) | 0); // per-day noise
  const pos = signal * (0.6 + 0.4 * r) + (1 - signal) * (0.5 * r); // hot → [0.6,1], plain → [0,0.5]
  return Math.round(QTY_MIN + (max - QTY_MIN) * Math.max(0, Math.min(1, pos)));
}
export function buyLimit(market, good, level, day, seed0, daySeed) {
  // Surplus = price below base → more stock available to buy.
  const ratio = priceAt(market, good, day, seed0) / GOODS[good].base;
  const surplus = Math.max(0, Math.min(1, (1 - ratio) / (1 - 0.4)));
  return marketQty(market, good, level, daySeed, 0x9e3779b1, 0, surplus);
}
export function sellLimit(market, good, level, day, seed0, daySeed) {
  // Demand = price above base → the market will buy more from you.
  const ratio = priceAt(market, good, day, seed0) / GOODS[good].base;
  const demand = Math.max(0, Math.min(1, (ratio - 1) / (1.7 - 1)));
  return marketQty(market, good, level, daySeed, 0x85ebca77, 0x27d4eb2f, demand);
}

// Cash needed for the next level. The 2.1× step (down from 2.4×) makes levelling
// come noticeably faster — the gap compounds, so mid/late levels arrive much sooner.
export const levelThreshold = (level) => Math.round(10000 * Math.pow(2.1, Math.max(1, level) - 1));
export const unlockedMarkets = (level) => MARKETS.map((_, i) => i).filter((i) => MARKETS[i].unlock <= level);
export const visibleGoods = (level) => GOODS.map((_, i) => i).filter((i) => GOODS[i].unlock <= level);

// ── Contracts (goals) ────────────────────────────────────────────────────────
export const QUEST_SLOTS = 3;          // absolute max contracts at once
export const QUEST_CANCEL_LIMIT = 6;   // cancellations allowed per rolling window
export const QUEST_CANCEL_WINDOW = 30; // that window, in days
const QUEST_COOLDOWN_MIN = 3;          // a cancelled slot stays empty this many days…
const QUEST_COOLDOWN_MAX = 10;         // …up to this many (random)
const QUEST_QUICK_DAYS = 3;            // deliver within this many days of posting for the speed bonus
// Slots unlock with level: 1 to start, a 2nd at Level 5, a 3rd at Level 7.
export const questSlotsAllowed = (level) => (level >= 7 ? 3 : level >= 5 ? 2 : 1);
// Level needed to unlock the nth slot (1→1, 2→5, 3→7).
export const questSlotUnlockLevel = (n) => (n <= 1 ? 1 : n === 2 ? 5 : 7);
// How many run right now: the fewer of what's unlocked and what the player wants.
export const questSlotsActive = (s) => Math.min(questSlotsAllowed(s.level), Math.max(1, s.questSlotsWanted || QUEST_SLOTS));
export const upgradeCost = (h) => Math.round(400 + (h.perTend - 1) * 700 + (h.cap - HONEY_MAX) * 30 + (h.kind === "special" ? 800 : 0));
export const hiveCost = (typeIndex) => (HIVE_TYPES[typeIndex] ? HIVE_TYPES[typeIndex].cost : 0);
export const totalItems = (s) => s.inv.reduce((a, b) => a + b, 0);

// A hive's queen: productivity (0.8–1.6× honey) and disease resistance (0.7–1.3).
function makeQueen() {
  return {
    prod: Math.round((0.8 + Math.random() * 0.8) * 100) / 100,
    resist: Math.round((0.7 + Math.random() * 0.6) * 100) / 100,
  };
}
export const queenProd = (h) => (h.queen && h.queen.prod) || 1;
export const queenResist = (h) => (h.queen && h.queen.resist) || 1;

function makeHive(typeIndex, id) {
  const t = HIVE_TYPES[typeIndex] || HIVE_TYPES[0];
  return {
    id,
    name: `${shortName(t.good)} Hive`,
    kind: t.kind,
    good: t.good,
    health: 100,
    lastTendDay: 0,
    tends: 0,
    honey: 0,
    beeswax: 0,
    propolis: 0,
    honeycomb: 0,
    perTend: 1,
    honeyEvery: t.every,
    cap: HONEY_MAX,
    tendCost: t.tend,
    healCost: t.heal,
    disaster: "", // active disaster name, "" if healthy
    disDaysLeft: 0, // days of production freeze remaining
    disDaily: 0, // cost per day to treat
    disTreated: false, // treated today?
    disImmuneUntil: 0, // no new disasters before this day (grace after recovering)
    dead: false, // lost permanently to an ignored disaster
    insured: false, // pays out on loss if true
    invested: t.cost, // purchase price + every upgrade — the insurance basis
    insPayout: 0, // payout waiting to be collected when a lost hive is removed
    queen: makeQueen(), // productivity + disease-resistance traits
  };
}

const randomSeed = () => Math.floor(Math.random() * 0x7fffffff);

export function initialState() {
  return {
    started: true,
    level: 1,
    day: 1,
    cash: 2000,
    market: 0,
    cap: PANTRY_START,
    storageBuys: 0,
    autoDays: 0,
    daySeed: randomSeed(),
    seed0: randomSeed(), // stable per-game seed for the price-trend waves & events
    inv: new Array(GOODS.length).fill(0),
    bought: new Array(GOODS.length).fill(0),
    sold: new Array(GOODS.length).fill(0),
    ovenSlots: 0, // 0 = no oven; 1–4 simultaneous bakes
    ovenBatch: 1, // items produced per bake, up to 10
    ovenJobs: [], // [{ good, daysLeft, qty }]
    hives: [makeHive(0, 1)],
    high: 2000,
    seq: 2,
    stats: { sales: 0, crafted: 0, baked: 0, cured: 0 }, // for achievements
    ach: [], // earned achievement ids
    visited: [0], // market indices visited
    quests: [], // active contracts (filled lazily by refreshQuests)
    questCancels: [], // days on which contracts were cancelled (rolling-window cap)
    questSlotsWanted: QUEST_SLOTS, // how many to run at once (capped by what level unlocks)
    prestige: 0, // retirement count → permanent sale-income multiplier
    streak: 0, // daily-login streak
    streakDay: "", // last claimed day (YYYY-MM-DD)
    orders: [], // standing auto-sell rules [{ good, minPrice }]
    loan: 0, // outstanding bank debt (accrues daily interest)
    tut: 0, // guided-tutorial step (0 = start; -1 = done/skipped)
  };
}

const clone = (s) => ({
  ...s,
  inv: s.inv.slice(),
  bought: (s.bought || []).slice(),
  sold: (s.sold || []).slice(),
  ovenJobs: (s.ovenJobs || []).map((j) => ({ ...j })),
  hives: s.hives.map((h) => ({ ...h })),
  stats: { ...(s.stats || {}) },
  ach: (s.ach || []).slice(),
  visited: (s.visited || []).slice(),
  quests: Array.isArray(s.quests) ? s.quests.map((q) => (q ? { ...q } : q)) : [],
  questCancels: Array.isArray(s.questCancels) ? s.questCancels.slice() : [],
  orders: (s.orders || []).map((o) => ({ ...o })),
});
const money = (n) => `$${Math.round(n).toLocaleString()}`;

function applyLevelUps(st) {
  let leveled = false;
  while (st.cash >= levelThreshold(st.level)) {
    st.level += 1;
    leveled = true;
  }
  return leveled;
}

// Give a healthy hive a random disaster: a freeze of random length within the
// disaster's range, priced per-day at the hive's healCost. Production stops.
function strike(h) {
  const d = DISASTERS[Math.floor(Math.random() * DISASTERS.length)];
  const days = d.min + Math.floor(Math.random() * (d.max - d.min + 1));
  h.disaster = d.name;
  h.disDaysLeft = days;
  h.disDaily = h.healCost;
  h.disTreated = false;
  h.honey = 0;
  h.beeswax = 0;
  h.propolis = 0;
  h.honeycomb = 0;
  return d.name;
}

// One day passes. Existing disasters progress (treated → count down/cure;
// ignored → spread + hive lost). Then healthy hives may catch a new disaster —
// odds rise 5% for every day a hive has gone untended. Neglected hives decay.
function advanceDay(st) {
  st.day += 1;
  st.daySeed = randomSeed(); // fresh random prices & limits each day
  st.bought = new Array(GOODS.length).fill(0);
  st.sold = new Array(GOODS.length).fill(0);
  const events = { abandoned: [], struck: [], cured: [], lost: [], spread: [], baked: [] };

  // Advance oven bakes; finished jobs drop their batch into the pantry.
  if (Array.isArray(st.ovenJobs) && st.ovenJobs.length) {
    const still = [];
    for (const j of st.ovenJobs) {
      if (j.daysLeft > 1) {
        still.push({ ...j, daysLeft: j.daysLeft - 1 });
      } else {
        st.inv[j.good] += j.qty;
        events.baked.push({ good: j.good, qty: j.qty });
      }
    }
    st.ovenJobs = still;
  }

  // 1. Resolve disasters that existed at the start of the day.
  const diseased = st.hives.filter((h) => h.disaster && !h.dead);
  let spreads = 0;
  for (const h of diseased) {
    if (h.disTreated) {
      h.disDaysLeft -= 1;
      h.disTreated = false;
      if (h.disDaysLeft <= 0) {
        h.disaster = "";
        h.disDaysLeft = 0;
        h.disDaily = 0;
        h.health = 100;
        // A hive can't be tended while diseased, so treat the recovery day as
        // attended — otherwise `unattended` snowballs and re-strikes immediately.
        h.lastTendDay = st.day;
        h.disImmuneUntil = st.day + DISASTER_IMMUNE_DAYS;
        st.stats.cured = (st.stats.cured || 0) + 1;
        events.cured.push(h.name);
      }
    } else {
      // Ignored — the colony is lost, and the trouble may spread. An insured
      // hive banks its payout; it's collected when the hive is removed.
      h.dead = true;
      events.lost.push({ name: h.name, event: h.disaster });
      if (h.insured) {
        h.insPayout = insurancePayout(h);
        events.insPending = events.insPending || [];
        events.insPending.push({ name: h.name, payout: h.insPayout });
      }
      if (Math.random() < SPREAD_CHANCE) spreads += 1;
    }
  }

  // 2. Spread ignored disasters to still-healthy hives (skipping recovering ones).
  for (let i = 0; i < spreads; i++) {
    const healthy = st.hives.filter((x) => !x.disaster && !x.dead && st.day >= (x.disImmuneUntil || 0));
    if (!healthy.length) break;
    const victim = healthy[Math.floor(Math.random() * healthy.length)];
    events.spread.push({ name: victim.name, event: strike(victim) });
  }

  // 3. New disasters — chance escalates with days left unattended, is strongly
  //    damped by the queen's resistance, and skips hives still in their grace
  //    period after recovering from one.
  if (st.level >= DISASTER_LEVEL) {
    for (const h of st.hives) {
      if (h.disaster || h.dead) continue;
      if (st.day < (h.disImmuneUntil || 0)) continue;
      const unattended = Math.max(0, st.day - h.lastTendDay - 1);
      const resist = Math.pow(queenResist(h), DISASTER_RESIST_POWER);
      const chance = Math.min(DISASTER_MAX, (DISASTER_BASE + DISASTER_PER_DAY * unattended) / resist);
      if (Math.random() < chance) events.struck.push({ name: h.name, event: strike(h) });
    }
  }

  // 3b. Auto-tend & collect subscription — tends (free) and collects every
  //     healthy hive for the day, so those hives don't decay in step 4.
  if (st.autoDays > 0) {
    st.autoDays -= 1;
    const room = { left: Math.max(0, st.cap - totalItems(st)) };
    for (const h of st.hives) {
      if (h.disaster || h.dead || h.lastTendDay >= st.day) continue;
      h.lastTendDay = st.day;
      h.health = 100;
      h.tends += 1;
      if (h.tends % (h.honeyEvery || 1) === 0) h.honey = Math.min(h.honey + Math.max(1, Math.round(h.perTend * queenProd(h))), h.cap);
      if (h.tends % 3 === 0) h.beeswax += 1;
      if (h.tends % 6 === 0) h.honeycomb += 1;
      if (h.tends % 7 === 0) h.propolis += 1;
      collectHive(h, st.inv, room);
    }
  }

  // 4. Health decay + abandonment for healthy, un-tended hives.
  for (const h of st.hives) {
    if (h.disaster || h.dead || h.lastTendDay >= st.day) continue;
    h.health = Math.max(0, h.health - HEALTH_DECAY);
  }
  events.abandoned = st.hives.filter((h) => !h.disaster && !h.dead && h.health <= 0);
  st.hives = st.hives.filter((h) => h.disaster || h.dead || h.health > 0);

  // Contracts lapse if their deadline passed; the empty slot reposts next refresh.
  if (Array.isArray(st.quests)) {
    for (let i = 0; i < st.quests.length; i++) {
      const q = st.quests[i];
      if (q && Number.isFinite(q.good) && st.day > q.dueDay) {
        events.questExpired = q;
        st.quests[i] = null;
      }
    }
  }

  // Bank loan accrues daily interest.
  if (st.loan > 0) {
    const before = st.loan;
    st.loan = Math.round(st.loan * (1 + LOAN_RATE));
    events.interest = st.loan - before;
  }

  // Standing auto-sell orders fire at the new market/day when the price is met.
  if (Array.isArray(st.orders) && st.orders.length) {
    for (const o of st.orders) {
      if (!st.inv[o.good]) continue;
      const price = priceAt(st.market, o.good, st.day, st.seed0);
      if (price < o.minPrice) continue;
      const room = Math.max(0, sellLimit(st.market, o.good, st.level, st.day, st.seed0, st.daySeed) - (st.sold[o.good] || 0));
      const n = Math.min(st.inv[o.good], room);
      if (n < 1) continue;
      const rev = Math.round(price * n * prestigeMult(st));
      st.cash += rev;
      st.inv[o.good] -= n;
      st.sold[o.good] += n;
      st.stats.sales = (st.stats.sales || 0) + 1;
      events.autoSold = events.autoSold || [];
      events.autoSold.push({ good: o.good, n, rev });
    }
    if (events.autoSold) applyLevelUps(st);
  }
  return events;
}

export function buy(s, good, qty) {
  const price = priceAt(s.market, good, s.day, s.seed0);
  const remaining = Math.max(0, buyLimit(s.market, good, s.level, s.day, s.seed0, s.daySeed) - (s.bought[good] || 0));
  const room = Math.max(0, s.cap - totalItems(s));
  const n = Math.min(qty, Math.floor(s.cash / price), room, remaining);
  if (n < 1) {
    if (remaining < 1) return { state: s, ok: false, note: `Market's out of ${GOODS[good].name} today` };
    if (room < 1) return { state: s, ok: false, note: "Pantry full — buy more storage" };
    return { state: s, ok: false, note: "Not enough cash" };
  }
  const st = clone(s);
  st.cash -= price * n;
  st.inv[good] += n;
  st.bought[good] += n;
  return { state: st, ok: true, note: `Bought ${n} ${GOODS[good].name} for ${money(price * n)}` };
}

export function sell(s, good, qty) {
  const price = priceAt(s.market, good, s.day, s.seed0);
  const remaining = Math.max(0, sellLimit(s.market, good, s.level, s.day, s.seed0, s.daySeed) - (s.sold[good] || 0));
  const n = Math.min(qty, s.inv[good], remaining);
  if (n < 1) {
    if (s.inv[good] > 0 && remaining < 1) return { state: s, ok: false, note: `Market won't buy more ${GOODS[good].name} today` };
    return { state: s, ok: false, note: "Nothing to sell" };
  }
  const st = clone(s);
  const revenue = Math.round(price * n * prestigeMult(st)); // prestige boosts sale income
  st.cash += revenue;
  st.inv[good] -= n;
  st.sold[good] += n;
  st.stats.sales = (st.stats.sales || 0) + 1;
  // contract progress: deliver the requested good to a contract's target market.
  // A single sale counts toward at most one matching contract.
  let contract = null;
  if (Array.isArray(st.quests)) {
    for (let i = 0; i < st.quests.length; i++) {
      const q = st.quests[i];
      if (q && Number.isFinite(q.good) && good === q.good && st.market === q.market && st.day <= q.dueDay && q.progress < q.qty) {
        q.progress += n;
        if (q.progress >= q.qty) {
          // Speed bonus: fulfilled within QUEST_QUICK_DAYS of posting → up to +25%.
          const quick = st.day <= q.quickDay ? Math.round(q.reward * q.quickPct) : 0;
          const total = q.reward + quick;
          st.cash += total;
          contract = { reward: total, base: q.reward, quick, good: q.good };
          st.quests[i] = newQuest(st);
        }
        break;
      }
    }
  }
  const prev = st.level;
  applyLevelUps(st);
  st.high = Math.max(st.high, st.cash);
  return { state: st, ok: true, note: `Sold ${n} ${GOODS[good].name} for ${money(revenue)}`, leveledTo: st.level > prev ? st.level : null, contract };
}

// Visiting any unlocked market advances a day (revisit current = wait a day).
export function travel(s, market) {
  if (market < 0 || market >= MARKETS.length || MARKETS[market].unlock > s.level) return { state: s, ok: false, note: "Market locked" };
  const st = clone(s);
  const same = market === st.market;
  st.market = market;
  if (!st.visited.includes(market)) st.visited.push(market);
  const events = advanceDay(st);
  st.high = Math.max(st.high, st.cash);
  return { state: st, ok: true, note: same ? `Another day at ${MARKETS[market].name}` : `Traveled to ${MARKETS[market].name}`, ...events };
}

// Tend a healthy hive for the day. Costs money; fails if you can't afford it.
export function tend(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h) return { state: s, ok: false };
  if (h.dead) return { state: s, ok: false, note: `${h.name} is lost` };
  if (h.disaster) return { state: s, ok: false, note: `${h.name} has ${h.disaster} — treat it first` };
  if (h.lastTendDay >= s.day) return { state: s, ok: false, note: `${h.name} already tended today` };
  if (s.cash < h.tendCost) return { state: s, ok: false, note: `Need ${money(h.tendCost)} to tend ${h.name}` };
  const st = clone(s);
  const hh = st.hives.find((x) => x.id === id);
  st.cash -= hh.tendCost;
  hh.lastTendDay = st.day;
  hh.health = 100;
  hh.tends += 1;
  if (hh.tends % (hh.honeyEvery || 1) === 0) hh.honey = Math.min(hh.honey + Math.max(1, Math.round(hh.perTend * queenProd(hh))), hh.cap);
  if (hh.tends % 3 === 0) hh.beeswax += 1;
  if (hh.tends % 6 === 0) hh.honeycomb += 1;
  if (hh.tends % 7 === 0) hh.propolis += 1;
  return { state: st, ok: true, note: `Tended ${hh.name} (−${money(hh.tendCost)})` };
}

// Tend every eligible hive at once — carries a 5% convenience surcharge.
export function tendAll(s) {
  const st = clone(s);
  let n = 0;
  let spent = 0;
  for (const h of st.hives) {
    if (h.dead || h.disaster || h.lastTendDay >= st.day) continue; // skip lost/diseased/done
    const cost = Math.round(h.tendCost * (1 + BULK_FEE)); // +5% bulk fee
    if (st.cash < cost) continue; // skip what we can't afford
    st.cash -= cost;
    spent += cost;
    h.lastTendDay = st.day;
    h.health = 100;
    h.tends += 1;
    if (h.tends % (h.honeyEvery || 1) === 0) h.honey = Math.min(h.honey + Math.max(1, Math.round(h.perTend * queenProd(h))), h.cap);
    if (h.tends % 3 === 0) h.beeswax += 1;
    if (h.tends % 6 === 0) h.honeycomb += 1;
    if (h.tends % 7 === 0) h.propolis += 1;
    n++;
  }
  if (!n) return { state: s, ok: false, note: "Nothing to tend (or can't afford it)" };
  return { state: st, ok: true, note: `Tended ${n} hive${n > 1 ? "s" : ""} for ${money(spent)} (incl. 5% fee)` };
}

// Pay a day's treatment for an active disaster (keeps it from being lost and
// counts down toward a cure on the next day).
export function treatDisaster(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h || !h.disaster || h.dead) return { state: s, ok: false };
  if (h.disTreated) return { state: s, ok: false, note: `${h.name} already treated today` };
  const cost = treatCost(h);
  if (s.cash < cost) return { state: s, ok: false, note: `Need ${money(cost)} to treat ${h.name}` };
  const st = clone(s);
  const hh = st.hives.find((x) => x.id === id);
  st.cash -= cost;
  hh.disTreated = true;
  return { state: st, ok: true, note: `Treated ${hh.name} (−${money(cost)}) — ${hh.disDaysLeft} day${hh.disDaysLeft > 1 ? "s" : ""} to go` };
}

// Pay all remaining treatment days at once (plus the instant-resolve fee).
export function resolveDisaster(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h || !h.disaster || h.dead) return { state: s, ok: false };
  const cost = instantCost(h);
  if (s.cash < cost) return { state: s, ok: false, note: `Need ${money(cost)} to resolve ${h.name}` };
  const st = clone(s);
  const hh = st.hives.find((x) => x.id === id);
  st.cash -= cost;
  hh.disaster = "";
  hh.disDaysLeft = 0;
  hh.disDaily = 0;
  hh.disTreated = false;
  hh.health = 100;
  hh.lastTendDay = st.day; // recovered today — don't count the sick days as neglect
  hh.disImmuneUntil = st.day + DISASTER_IMMUNE_DAYS;
  st.stats.cured = (st.stats.cured || 0) + 1;
  return { state: st, ok: true, note: `${hh.name} fully restored! 🐝 (−${money(cost)})` };
}

// Clear a hive that was lost to an ignored disaster, freeing the apiary slot.
export function removeHive(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h || !h.dead) return { state: s, ok: false };
  const st = clone(s);
  st.hives = st.hives.filter((x) => x.id !== id);
  // Clearing the hive is when the insurance claim actually settles.
  const payout = Math.max(0, h.insPayout || 0);
  if (payout > 0) {
    st.cash += payout;
    st.high = Math.max(st.high, st.cash);
    return { state: st, ok: true, note: `Removed ${h.name} — insurance paid ${money(payout)} 🛡️`, insPaid: { name: h.name, payout } };
  }
  return { state: st, ok: true, note: `Removed ${h.name}` };
}

// Move one resource from a hive into the pantry, skimming a handling fee first
// (feeRate>0) and honouring remaining pantry room. Returns amount taken, updates
// the hive's stored count, and flags leftover if the pantry filled up.
function drain(amount, slot, inv, room, feeRate, state) {
  const fee = Math.round(amount * feeRate); // skimmed off the top and discarded
  const collectable = amount - fee;
  const taken = Math.min(collectable, room.left);
  inv[slot] += taken;
  room.left -= taken;
  const remain = collectable - taken; // didn't fit — stays in the hive
  if (remain > 0) state.leftover = true;
  state.feeLost += fee;
  return { taken, remain };
}

function collectHive(h, inv, room, feeRate = 0) {
  const parts = [];
  const state = { leftover: false, feeLost: 0 };
  const c1 = drain(h.honey, h.good, inv, room, feeRate, state);
  h.honey = c1.remain;
  if (c1.taken) parts.push(`${c1.taken} ${GOODS[h.good].name}`);
  const c2 = drain(h.beeswax, BEESWAX, inv, room, feeRate, state);
  h.beeswax = c2.remain;
  if (c2.taken) parts.push(`${c2.taken} beeswax`);
  const c3 = drain(h.propolis, PROPOLIS, inv, room, feeRate, state);
  h.propolis = c3.remain;
  if (c3.taken) parts.push(`${c3.taken} propolis`);
  const c4 = drain(h.honeycomb || 0, HONEYCOMB, inv, room, feeRate, state);
  h.honeycomb = c4.remain;
  if (c4.taken) parts.push(`${c4.taken} honeycomb`);
  return { parts, leftover: state.leftover, feeLost: state.feeLost };
}

export function collect(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h || h.honey + h.beeswax + h.propolis + (h.honeycomb || 0) <= 0) return { state: s, ok: false, note: "Nothing to collect" };
  if (s.cap - totalItems(s) < 1) return { state: s, ok: false, note: "Pantry full — buy jars or storage" };
  const st = clone(s);
  const hh = st.hives.find((x) => x.id === id);
  const room = { left: Math.max(0, st.cap - totalItems(st)) };
  const { parts, leftover } = collectHive(hh, st.inv, room);
  return { state: st, ok: true, note: `Collected ${parts.join(", ")}${leftover ? " (pantry full — rest left in hive)" : ""}` };
}

// Collect from every hive at once — a 5% handling fee is skimmed off the goods.
export function collectAll(s) {
  if (s.cap - totalItems(s) < 1) return { state: s, ok: false, note: "Pantry full — buy more storage" };
  const st = clone(s);
  const room = { left: Math.max(0, st.cap - totalItems(st)) };
  let any = false;
  let leftover = false;
  let fee = 0;
  for (const h of st.hives) {
    if (h.honey + h.beeswax + h.propolis + (h.honeycomb || 0) <= 0) continue;
    const r = collectHive(h, st.inv, room, BULK_FEE);
    if (r.parts.length) any = true;
    if (r.leftover) leftover = true;
    fee += r.feeLost;
    if (room.left <= 0) {
      leftover = leftover || st.hives.some((x) => x.honey + x.beeswax + x.propolis + (x.honeycomb || 0) > 0);
      break;
    }
  }
  if (!any) return { state: s, ok: false, note: "Nothing to collect" };
  return { state: st, ok: true, note: `Collected from your hives${fee ? ` (5% fee: ${fee} item${fee > 1 ? "s" : ""})` : ""}${leftover ? " · pantry full, some left" : ""}` };
}

export function renameHive(s, id, name) {
  const st = clone(s);
  const h = st.hives.find((x) => x.id === id);
  if (!h) return { state: s, ok: false };
  h.name = String(name || "").trim().slice(0, 24) || `Hive ${id}`;
  return { state: st, ok: true };
}

export function upgradeHive(s, id) {
  const st = clone(s);
  const h = st.hives.find((x) => x.id === id);
  if (!h) return { state: s, ok: false };
  const cost = upgradeCost(h);
  if (st.cash < cost) return { state: s, ok: false, note: "Not enough cash to upgrade" };
  st.cash -= cost;
  h.invested = (h.invested || 0) + cost; // upgrades count toward the insurance payout
  h.perTend += 1;
  h.cap += 5;
  h.beeswax += 1;
  h.propolis += 1;
  h.health = 100;
  return { state: st, ok: true, note: `Upgraded ${h.name} — +${h.perTend} honey/tend, +1 🕯️ +1 🧪, holds ${h.cap}` };
}

export function buyHive(s, typeIndex) {
  const t = HIVE_TYPES[typeIndex];
  if (!t) return { state: s, ok: false };
  if (s.hives.length >= MAX_HIVES) return { state: s, ok: false, note: "Apiary is full" };
  if (s.cash < t.cost) return { state: s, ok: false, note: "Not enough cash" };
  const st = clone(s);
  st.cash -= t.cost;
  const nh = makeHive(typeIndex, st.seq);
  nh.lastTendDay = st.day; // a fresh hive starts attended (no instant disaster risk)
  st.hives.push(nh);
  st.seq += 1;
  return { state: st, ok: true, note: `Bought a ${shortName(t.good)} hive` };
}

export function craft(s, recipeIndex, qty = 1) {
  const rec = RECIPES[recipeIndex];
  if (!rec) return { state: s, ok: false };
  if (s.level < rec.level) return { state: s, ok: false, note: `Unlocks at Level ${rec.level}` };
  let n = qty;
  for (const ing of rec.ins) n = Math.min(n, Math.floor(s.inv[ing.good] / ing.qty));
  if (n < 1) {
    const short = rec.ins.find((ing) => s.inv[ing.good] < ing.qty);
    return { state: s, ok: false, note: `Need ${short.qty} ${GOODS[short.good].name}` };
  }
  const st = clone(s);
  for (const ing of rec.ins) st.inv[ing.good] -= ing.qty * n;
  st.inv[rec.out] += n;
  st.stats.crafted = (st.stats.crafted || 0) + n;
  return { state: st, ok: true, note: `Crafted ${n} ${GOODS[rec.out].name}` };
}

export function buyStorage(s) {
  const cost = storageCost(s);
  if (s.cash < cost) return { state: s, ok: false, note: "Not enough cash" };
  const st = clone(s);
  st.cash -= cost;
  st.cap += STORAGE_ADD;
  st.storageBuys = (st.storageBuys || 0) + 1;
  return { state: st, ok: true, note: `Bought storage — pantry holds ${st.cap} (next: ${money(storageCost(st))})` };
}

// Buy an auto-tend & collect plan (adds days). Available from Level 7.
export function buyAuto(s, planIndex) {
  const plan = AUTO_PLANS[planIndex];
  if (!plan) return { state: s, ok: false };
  if (s.level < AUTO_LEVEL) return { state: s, ok: false, note: `Unlocks at Level ${AUTO_LEVEL}` };
  if (s.cash < plan.cost) return { state: s, ok: false, note: "Not enough cash" };
  const st = clone(s);
  st.cash -= plan.cost;
  st.autoDays = (st.autoDays || 0) + plan.days;
  return { state: st, ok: true, note: `Auto-tend active for ${st.autoDays} day${st.autoDays > 1 ? "s" : ""}` };
}

/* ── Oven / stove (Level 10+) ──────────────────────────────────────────────── */

export function buyOven(s) {
  if ((s.ovenSlots || 0) >= 1) return { state: s, ok: false, note: "You already own an oven" };
  if (s.level < 10) return { state: s, ok: false, note: "Ovens unlock at Level 10" };
  if (s.cash < OVEN_COST) return { state: s, ok: false, note: "Not enough cash" };
  const st = clone(s);
  st.cash -= OVEN_COST;
  st.ovenSlots = 1;
  st.ovenBatch = 1;
  return { state: st, ok: true, note: "Bought a stove/oven — start baking! 🔥" };
}

export function upgradeOvenSlots(s) {
  const slots = s.ovenSlots || 0;
  if (slots < 1) return { state: s, ok: false, note: "Buy an oven first" };
  if (slots >= OVEN_MAX_SLOTS) return { state: s, ok: false, note: "You're at the maximum number of ovens" };
  if (slots >= ovenSlotsAllowed(s.level)) return { state: s, ok: false, note: `Reach Level ${ovenSlotUnlockLevel(slots + 1)} to add another oven` };
  const cost = ovenSlotUpgradeCost(slots);
  if (s.cash < cost) return { state: s, ok: false, note: "Not enough cash" };
  const st = clone(s);
  st.cash -= cost;
  st.ovenSlots = slots + 1;
  return { state: st, ok: true, note: `Bought another oven — now baking ${st.ovenSlots} items at once 🔥` };
}

export function upgradeOvenBatch(s) {
  const batch = s.ovenBatch || 1;
  if ((s.ovenSlots || 0) < 1) return { state: s, ok: false, note: "Buy an oven first" };
  if (batch >= OVEN_MAX_BATCH) return { state: s, ok: false, note: "Batch size is maxed" };
  if (s.cash < OVEN_BATCH_COST) return { state: s, ok: false, note: "Not enough cash" };
  const st = clone(s);
  st.cash -= OVEN_BATCH_COST;
  st.ovenBatch = batch + 1;
  return { state: st, ok: true, note: `Oven batch size now ${st.ovenBatch}` };
}

// Start baking an oven item. Consumes ingredients × batch; finishes in `bake` days.
export function startBake(s, ovenIndex) {
  const rec = OVEN_ITEMS[ovenIndex];
  if (!rec) return { state: s, ok: false };
  if ((s.ovenSlots || 0) < 1) return { state: s, ok: false, note: "Buy an oven first" };
  if ((s.ovenJobs || []).length >= s.ovenSlots) return { state: s, ok: false, note: "All oven slots are busy" };
  const batch = s.ovenBatch || 1;
  const short = rec.ins.find((ing) => (s.inv[ing.good] || 0) < ing.qty * batch);
  if (short) return { state: s, ok: false, note: `Need ${short.qty * batch} ${GOODS[short.good].name}` };
  const st = clone(s);
  for (const ing of rec.ins) st.inv[ing.good] -= ing.qty * batch;
  st.ovenJobs.push({ good: rec.out, daysLeft: rec.bake, qty: batch });
  st.stats.baked = (st.stats.baked || 0) + 1;
  return { state: st, ok: true, note: `Baking ${batch} ${GOODS[rec.out].name} — ready in ${rec.bake} day${rec.bake > 1 ? "s" : ""}` };
}

export function sanitize(s) {
  if (!s || !s.started) return null;
  const clampArr = (src) => {
    const a = new Array(GOODS.length).fill(0);
    if (Array.isArray(src)) for (let i = 0; i < GOODS.length; i++) a[i] = Math.max(0, Math.min(9_999_999, src[i] | 0));
    return a;
  };
  const inv = clampArr(s.inv);
  const bought = clampArr(s.bought);
  const sold = clampArr(s.sold);
  const ovenJobs = (Array.isArray(s.ovenJobs) ? s.ovenJobs : [])
    .slice(0, OVEN_MAX_SLOTS)
    .map((j) => ({
      good: Math.max(0, Math.min(GOODS.length - 1, j.good | 0)),
      daysLeft: Math.max(0, Math.min(30, j.daysLeft | 0)),
      qty: Math.max(1, Math.min(OVEN_MAX_BATCH, j.qty | 0)),
    }))
    .filter((j) => GOODS[j.good] && GOODS[j.good].kind === "oven");
  const hives = (Array.isArray(s.hives) ? s.hives : []).slice(0, MAX_HIVES).map((h, i) => {
    const good = Math.max(0, Math.min(GOODS.length - 1, h.good | 0));
    const t = typeByGood(good);
    return {
      id: Number.isFinite(h.id) ? h.id : i + 1,
      name: typeof h.name === "string" && h.name.trim() ? h.name.slice(0, 24) : `Hive ${h.id ?? i + 1}`,
      kind: h.kind === "special" ? "special" : "standard",
      good,
      health: Math.max(0, Math.min(100, h.health == null ? 100 : h.health | 0)),
      lastTendDay: Math.max(0, Math.min(1_000_000, h.lastTendDay | 0)),
      tends: Math.max(0, Math.min(9_999_999, h.tends | 0)),
      honey: Math.max(0, Math.min(9_999_999, h.honey | 0)),
      beeswax: Math.max(0, Math.min(9_999_999, h.beeswax | 0)),
      propolis: Math.max(0, Math.min(9_999_999, h.propolis | 0)),
      honeycomb: Math.max(0, Math.min(9_999_999, h.honeycomb | 0)),
      perTend: Math.max(1, Math.min(9999, h.perTend == null ? 1 : h.perTend | 0)),
      honeyEvery: Math.max(1, Math.min(1000, h.honeyEvery == null ? t.every : h.honeyEvery | 0)),
      cap: Math.max(1, Math.min(100000, h.cap == null ? HONEY_MAX : h.cap | 0)),
      tendCost: Math.max(0, Math.min(10_000_000, h.tendCost == null ? t.tend : h.tendCost | 0)),
      healCost: Math.max(0, Math.min(1_000_000_000, h.healCost == null ? t.heal : h.healCost | 0)),
      disaster: typeof h.disaster === "string" ? h.disaster.slice(0, 60) : "",
      disDaysLeft: Math.max(0, Math.min(1000, h.disDaysLeft | 0)),
      disDaily: Math.max(0, Math.min(1_000_000_000, h.disDaily | 0)),
      disTreated: !!h.disTreated,
      disImmuneUntil: Math.max(0, Math.min(1_000_000, h.disImmuneUntil | 0)),
      dead: !!h.dead,
      insured: !!h.insured,
      invested: Math.max(0, Math.min(1e12, h.invested == null ? t.cost : +h.invested || 0)),
      insPayout: Math.max(0, Math.min(1e12, +h.insPayout || 0)),
      queen: {
        prod: Math.max(0.5, Math.min(3, +(h.queen && h.queen.prod) || 1)),
        resist: Math.max(0.5, Math.min(3, +(h.queen && h.queen.resist) || 1)),
      },
    };
  });
  const st0 = s.stats || {};
  // Contracts: accept the new `quests` array, or migrate a legacy single `quest`.
  const rawQuests = Array.isArray(s.quests) ? s.quests : s.quest ? [s.quest] : [];
  const quests = rawQuests.slice(0, QUEST_SLOTS).map((q) => {
    if (q && q.cooldownUntil != null && !Number.isFinite(q.good)) {
      return { cooldownUntil: Math.max(0, Math.min(1_000_000, q.cooldownUntil | 0)) };
    }
    if (q && Number.isFinite(q.good) && Number.isFinite(q.market)) {
      const postedDay = Math.max(0, Math.min(1_000_000, q.postedDay == null ? s.day | 0 : q.postedDay | 0));
      const quickPct = +q.quickPct >= 0 ? Math.min(0.25, +q.quickPct) : 0.25;
      return {
        good: Math.max(0, Math.min(GOODS.length - 1, q.good | 0)),
        market: Math.max(0, Math.min(MARKETS.length - 1, q.market | 0)),
        qty: Math.max(1, Math.min(999999, q.qty | 0)),
        progress: Math.max(0, Math.min(999999, q.progress | 0)),
        dueDay: Math.max(0, Math.min(1_000_000, q.dueDay | 0)),
        reward: Math.max(0, Math.min(1e10, q.reward | 0)),
        postedDay,
        quickDay: Math.max(0, Math.min(1_000_000, q.quickDay == null ? postedDay + QUEST_QUICK_DAYS : q.quickDay | 0)),
        quickPct,
      };
    }
    return null;
  });
  const questCancels = (Array.isArray(s.questCancels) ? s.questCancels : [])
    .map((d) => d | 0)
    .filter((d) => Number.isFinite(d))
    .slice(-50);
  const questSlotsWanted = Number.isFinite(s.questSlotsWanted) ? Math.max(1, Math.min(QUEST_SLOTS, s.questSlotsWanted | 0)) : QUEST_SLOTS;
  return {
    started: true,
    level: Math.max(1, Math.min(100, s.level | 0)),
    day: Math.max(1, Math.min(1_000_000, s.day | 0)),
    cash: Math.max(0, Math.min(1e12, +s.cash || 0)),
    market: Math.max(0, Math.min(MARKETS.length - 1, s.market | 0)),
    cap: Math.max(PANTRY_START, Math.min(1e9, s.cap == null ? PANTRY_START : s.cap | 0)),
    storageBuys: Math.max(0, Math.min(100000, s.storageBuys | 0)),
    autoDays: Math.max(0, Math.min(100000, s.autoDays | 0)),
    daySeed: Number.isFinite(s.daySeed) ? (s.daySeed | 0) & 0x7fffffff : (Math.imul(s.day | 0, 2654435761) >>> 0) & 0x7fffffff,
    seed0: Number.isFinite(s.seed0) ? (s.seed0 | 0) & 0x7fffffff : 0x5eedbee,
    ovenSlots: Math.max(0, Math.min(OVEN_MAX_SLOTS, s.ovenSlots | 0)),
    ovenBatch: Math.max(1, Math.min(OVEN_MAX_BATCH, s.ovenBatch == null ? 1 : s.ovenBatch | 0)),
    ovenJobs,
    inv,
    bought,
    sold,
    hives,
    high: Math.max(0, Math.min(1e12, +s.high || 0)),
    seq: Math.max(1, Math.min(1e9, s.seq | 0)),
    stats: {
      sales: Math.max(0, Math.min(1e9, st0.sales | 0)),
      crafted: Math.max(0, Math.min(1e9, st0.crafted | 0)),
      baked: Math.max(0, Math.min(1e9, st0.baked | 0)),
      cured: Math.max(0, Math.min(1e9, st0.cured | 0)),
    },
    ach: Array.isArray(s.ach) ? s.ach.filter((x) => typeof x === "string").slice(0, 100) : [],
    visited: Array.isArray(s.visited) ? [...new Set(s.visited.map((x) => x | 0).filter((x) => x >= 0 && x < MARKETS.length))] : [Math.max(0, Math.min(MARKETS.length - 1, s.market | 0))],
    quests,
    questCancels,
    questSlotsWanted,
    prestige: Math.max(0, Math.min(9999, s.prestige | 0)),
    streak: Math.max(0, Math.min(100000, s.streak | 0)),
    streakDay: typeof s.streakDay === "string" ? s.streakDay.slice(0, 10) : "",
    orders: (Array.isArray(s.orders) ? s.orders : [])
      .filter((o) => o && Number.isFinite(o.good) && o.good >= 0 && o.good < GOODS.length)
      .slice(0, 8)
      .map((o) => ({ good: o.good | 0, minPrice: Math.max(1, Math.min(1e9, o.minPrice | 0)) })),
    loan: Math.max(0, Math.min(1e12, Math.round(+s.loan || 0))),
    tut: Number.isFinite(s.tut) ? Math.max(-1, Math.min(50, s.tut | 0)) : -1, // legacy saves skip the tutorial
  };
}

/* ── Long-term goals: contracts, achievements, prestige & insurance ──────────── */

function hiveValue(h) {
  return (typeByGood(h.good) || HIVE_TYPES[0]).cost;
}

export function netWorth(s) {
  let v = s.cash;
  for (let i = 0; i < GOODS.length; i++) v += (s.inv[i] || 0) * GOODS[i].base;
  for (const h of s.hives) if (!h.dead) v += hiveValue(h);
  v -= s.loan || 0; // debt counts against you
  return Math.round(v);
}

// Prestige — each retirement permanently boosts sale income.
export const PRESTIGE_STEP = 0.15;
export const RETIRE_MIN = 250000;
export function prestigeMult(s) {
  return 1 + (s.prestige || 0) * PRESTIGE_STEP;
}
export function canRetire(s) {
  return netWorth(s) >= RETIRE_MIN;
}
/* ── Retirement offers ────────────────────────────────────────────────────────
   Selling up isn't a fixed payout: three buyers bid for the apiary. Each pays a
   random 35–85% of what you sank into your hives, and sweetens a low cash offer
   with a bigger crate of honey & supplies (and vice versa) — so the choice is
   cash-now versus stock to trade with. */
export const RETIRE_OFFERS = 3;
const RETIRE_PCT_MIN = 0.35;
const RETIRE_PCT_MAX = 0.85;
const RETIRE_MAX_ITEMS = 4000; // last-resort ceiling so a crate stays haulable
// Which goods a crate is drawn from, by how much value it has to carry. Bigger
// budgets are paid in premium stock so the item count stays sane — capping
// quantities instead would flatten the cash-vs-goods trade-off entirely.
const RETIRE_TIERS = [
  { min: 150000, goods: [5, 4, PROPOLIS, 3] }, // Royal Jelly / Manuka / Propolis / Buckwheat
  { min: 40000, goods: [4, 3, 2, PROPOLIS, HONEYCOMB] },
  { min: 0, goods: [0, 1, 2, 3, BEESWAX, HONEYCOMB, PROPOLIS] },
];

// What a buyer is actually purchasing: everything sunk into the living hives.
export const apiaryValue = (s) => (s.hives || []).filter((h) => !h.dead).reduce((a, h) => a + hiveInvested(h), 0);

// Turn a cash-equivalent budget into a crate of 3–5 goods.
function buildCrate(budget) {
  if (budget < 1) return [];
  const pool = (RETIRE_TIERS.find((t) => budget >= t.min) || RETIRE_TIERS[RETIRE_TIERS.length - 1]).goods.slice();
  const n = Math.min(pool.length, 3 + Math.floor(Math.random() * 3));
  const picks = [];
  for (let i = 0; i < n && pool.length; i++) picks.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  const weights = picks.map(() => 0.5 + Math.random());
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  let crate = picks
    .map((good, i) => ({ good, qty: Math.floor((budget * (weights[i] / total)) / GOODS[good].base) }))
    .filter((x) => x.qty > 0);
  const items = crate.reduce((a, c) => a + c.qty, 0);
  if (items > RETIRE_MAX_ITEMS) {
    const scale = RETIRE_MAX_ITEMS / items;
    crate = crate.map((c) => ({ ...c, qty: Math.max(1, Math.floor(c.qty * scale)) }));
  }
  return crate;
}

// Three bids for the apiary. Generate once when the player opens the chooser —
// re-rolling on every render would let them reshuffle for a better price.
export function retirementOffers(s) {
  const value = apiaryValue(s);
  return Array.from({ length: RETIRE_OFFERS }, () => {
    const pct = RETIRE_PCT_MIN + Math.random() * (RETIRE_PCT_MAX - RETIRE_PCT_MIN);
    const goods = buildCrate(value * Math.max(0.05, 0.95 - pct)); // cheaper cash offer → fatter crate
    return {
      pct: Math.round(pct * 100) / 100,
      cash: Math.round(value * pct),
      goods,
      goodsValue: goods.reduce((a, c) => a + c.qty * GOODS[c.good].base, 0),
    };
  });
}

// Settling an outstanding loan out of the sale proceeds. Whatever isn't cleared
// follows you into the next run (debt doesn't evaporate just because you retired).
export function retireSettlement(offer, loan, payLoan) {
  const proceeds = Math.max(0, Math.round((offer && offer.cash) || 0));
  const debt = Math.max(0, Math.round(loan || 0));
  const paid = payLoan ? Math.min(debt, proceeds) : 0;
  return { paid, cash: proceeds - paid, debt: debt - paid };
}

export function retire(s, offer, { payLoan = false } = {}) {
  if (!canRetire(s)) return { state: s, ok: false, note: `Reach ${money(RETIRE_MIN)} net worth to retire` };
  const g = initialState();
  g.prestige = (s.prestige || 0) + 1;
  g.ach = (s.ach || []).slice(); // achievements carry over
  g.streak = s.streak || 0;
  g.streakDay = s.streakDay || "";
  g.cash = 2000 + g.prestige * 3000; // the apprentice's nest egg
  const { paid, cash, debt } = retireSettlement(offer, s.loan, payLoan);
  g.loan = debt; // anything the sale didn't cover carries over
  if (offer) {
    g.cash += cash;
    for (const c of offer.goods || []) {
      if (GOODS[c.good]) g.inv[c.good] += Math.max(0, c.qty | 0);
    }
    // Make sure the parting crate actually fits in the new pantry.
    const items = g.inv.reduce((a, b) => a + b, 0);
    if (items > g.cap) g.cap = Math.ceil((items + 50) / 50) * 50;
  }
  const sold = offer ? ` Sold the apiary for ${money(offer.cash || 0)}${(offer.goods || []).length ? " plus a crate of stock" : ""}.` : "";
  const settled = paid > 0 ? ` Paid ${money(paid)} off the loan${debt > 0 ? `, ${money(debt)} carried over` : " — debt clear"}.` : debt > 0 ? ` ${money(debt)} of debt carried over.` : "";
  return { state: g, ok: true, note: `Retired! Apprentice #${g.prestige} keeps a +${Math.round((prestigeMult(g) - 1) * 100)}% sale bonus.${sold}${settled}` };
}

// Everything sunk into a hive (purchase + upgrades); older saves fall back to the
// hive type's list price.
export const hiveInvested = (h) => Math.max(0, (h && h.invested) || hiveValue(h));
// Insurance pays back 10× that, collected when the lost hive is removed.
export const insurancePayout = (h) => Math.round(hiveInvested(h) * INSURANCE_PAYOUT_MULT);

// Insurance — a one-time premium, priced as a share of the payout it buys.
// Also cuts disaster recovery costs by 90%.
export const insuranceCost = (h) => Math.round(insurancePayout(h) * INSURANCE_PREMIUM_RATE);
export function insureHive(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h || h.dead) return { state: s, ok: false };
  if (h.insured) return { state: s, ok: false, note: "Already insured" };
  // No insuring your way out of a disaster already in progress.
  if (h.disaster) return { state: s, ok: false, note: `${h.name} can't be insured mid-disaster — clear it first` };
  const cost = insuranceCost(h);
  if (s.cash < cost) return { state: s, ok: false, note: `Need ${money(cost)} to insure ${h.name}` };
  const st = clone(s);
  const hh = st.hives.find((x) => x.id === id);
  st.cash -= cost;
  hh.insured = true;
  return { state: st, ok: true, note: `${hh.name} insured — pays ${money(insurancePayout(hh))} if lost, and 90% off recovery` };
}

// Contracts — deliver a good to a market by a deadline for a cash bonus.
export function newQuest(s) {
  const deliverable = visibleGoods(s.level).filter((g) => GOODS[g].kind !== "oven");
  const good = deliverable.length ? deliverable[Math.floor(Math.random() * deliverable.length)] : 0;
  const unlocked = unlockedMarkets(s.level);
  const others = unlocked.filter((m) => m !== s.market);
  const pool = others.length ? others : unlocked;
  const market = pool[Math.floor(Math.random() * pool.length)];
  // Contracts get more demanding as you level up: the quantity floor climbs with
  // level (~1.4 units/level) on top of a random spread. The bulk multiplier also
  // ramps in with level — ×3 for approachable early contracts, growing to ×10 by
  // mid-game (level ~15+) — so early hauls stay manageable.
  const bulk = Math.min(10, 3 + (s.level - 1) * 0.5);
  const qty = Math.round((5 + Math.floor(s.level * 1.4) + Math.floor(Math.random() * (8 + s.level))) * bulk);
  // ...and the payoff grows to match: the per-unit bonus multiplier rises with
  // level (capped at 6×), so a bigger delivery also pays a much larger bonus.
  const bonusMult = Math.min(6, 1.6 + s.level * 0.07 + Math.random() * 0.9);
  // A little extra deadline slack for the larger high-level hauls.
  const dueDay = s.day + 6 + Math.floor(s.level / 8) + Math.floor(Math.random() * 9);
  const reward = Math.round(qty * GOODS[good].base * bonusMult);
  // Speed bonus: fulfil within QUEST_QUICK_DAYS of posting for an extra 10–25%.
  const quickPct = 0.1 + Math.random() * 0.15;
  return { good, market, qty, progress: 0, dueDay, reward, postedDay: s.day, quickDay: s.day + QUEST_QUICK_DAYS, quickPct };
}

// Keep the contract board full: fill every empty slot with a fresh contract,
// except slots still cooling down from a cancellation. The number of slots is the
// active count (unlocked-by-level, capped by the player's preference). Used lazily
// after load and after each action.
export function refreshQuests(s) {
  const st = clone(s);
  if (!Array.isArray(st.quests)) st.quests = [];
  const N = questSlotsActive(st);
  if (st.quests.length > N) {
    // Shrinking the board (levelled expectation or the player's preference):
    // keep active contracts first so in-progress work isn't thrown away.
    const active = st.quests.filter((q) => q && Number.isFinite(q.good));
    const rest = st.quests.filter((q) => !(q && Number.isFinite(q.good)));
    st.quests = [...active, ...rest].slice(0, N);
  }
  while (st.quests.length < N) st.quests.push(null);
  for (let i = 0; i < N; i++) {
    const slot = st.quests[i];
    const isContract = slot && Number.isFinite(slot.good);
    const cooling = slot && slot.cooldownUntil != null && st.day < slot.cooldownUntil;
    if (!isContract && !cooling) st.quests[i] = newQuest(st);
  }
  return st;
}

// Set how many contracts to run at once (clamped to what's unlocked at your level).
export function setQuestSlots(s, n) {
  const wanted = Math.max(1, Math.min(QUEST_SLOTS, n | 0));
  const st = refreshQuests({ ...clone(s), questSlotsWanted: wanted });
  const active = questSlotsActive(st);
  return { state: st, ok: true, note: `Running ${active} contract${active === 1 ? "" : "s"} at a time` };
}

// How many cancellations count against the rolling-window cap right now.
export function questCancelsUsed(s) {
  return (s.questCancels || []).filter((d) => s.day - d < QUEST_CANCEL_WINDOW).length;
}
export function canCancelQuest(s) {
  return questCancelsUsed(s) < QUEST_CANCEL_LIMIT;
}
// Cancel the contract in a slot: counts against the cap and leaves the slot empty
// (no repost) for a random 3–10 days.
export function cancelQuest(s, slotIndex) {
  const slot = s.quests && s.quests[slotIndex];
  if (!slot || !Number.isFinite(slot.good)) return { state: s, ok: false, note: "No contract to cancel there." };
  if (!canCancelQuest(s)) return { state: s, ok: false, note: `Cancel limit reached — ${QUEST_CANCEL_LIMIT} per ${QUEST_CANCEL_WINDOW} days.` };
  const st = clone(s);
  st.questCancels = [...(st.questCancels || []).filter((d) => st.day - d < QUEST_CANCEL_WINDOW), st.day];
  const cd = QUEST_COOLDOWN_MIN + Math.floor(Math.random() * (QUEST_COOLDOWN_MAX - QUEST_COOLDOWN_MIN + 1));
  st.quests[slotIndex] = { cooldownUntil: st.day + cd };
  return { state: st, ok: true, note: `Contract cancelled — a new one posts in ${cd} day${cd === 1 ? "" : "s"}.` };
}

// How many units a "Deliver" click would fulfil right now (0 if not deliverable).
export function deliverableQty(s, slotIndex) {
  const q = s.quests && s.quests[slotIndex];
  if (!q || !Number.isFinite(q.good)) return 0;
  if (s.market !== q.market || s.day > q.dueDay) return 0;
  return Math.min(q.qty - q.progress, s.inv[q.good] || 0);
}

// Deliver on-hand inventory straight to a contract at its target market. Unlike a
// normal sale this ISN'T capped by the market's daily sell limit — it's bounded by
// the contract's remaining quantity — so a 150-unit contract is fillable in one go.
// Delivered units still pay the current market price (delivery = selling there).
export function deliverContract(s, slotIndex) {
  const q = s.quests && s.quests[slotIndex];
  if (!q || !Number.isFinite(q.good)) return { state: s, ok: false, note: "No contract there." };
  if (s.day > q.dueDay) return { state: s, ok: false, note: "Contract expired." };
  if (s.market !== q.market) return { state: s, ok: false, note: `Travel to ${MARKETS[q.market].name} to deliver.` };
  const need = q.qty - q.progress;
  const have = s.inv[q.good] || 0;
  const n = Math.min(need, have);
  if (n < 1) return { state: s, ok: false, note: have < 1 ? `No ${GOODS[q.good].name} on hand to deliver.` : "Contract already filled." };
  const st = clone(s);
  const price = priceAt(st.market, q.good, st.day, st.seed0);
  const revenue = Math.round(price * n * prestigeMult(st)); // prestige boosts sale income
  st.cash += revenue;
  st.inv[q.good] -= n;
  st.stats.sales = (st.stats.sales || 0) + 1;
  const qq = st.quests[slotIndex];
  qq.progress += n;
  let contract = null;
  if (qq.progress >= qq.qty) {
    const quick = st.day <= qq.quickDay ? Math.round(qq.reward * qq.quickPct) : 0;
    const total = qq.reward + quick;
    st.cash += total;
    contract = { reward: total, base: qq.reward, quick, good: qq.good };
    st.quests[slotIndex] = newQuest(st);
  }
  const prev = st.level;
  applyLevelUps(st);
  st.high = Math.max(st.high, st.cash);
  return { state: st, ok: true, note: `Delivered ${n} ${GOODS[q.good].name} for ${money(revenue)}`, contract, leveledTo: st.level > prev ? st.level : null };
}

// Achievements — one-time milestones paying a cash reward.
export const ACHIEVEMENTS = [
  { id: "first_sale", icon: "💰", name: "First Sale", desc: "Sell your first good", reward: 500, check: (s) => (s.stats.sales || 0) >= 1 },
  { id: "busy_bees", icon: "🐝", name: "Busy Bees", desc: "Own 5 hives", reward: 5000, check: (s) => s.hives.filter((h) => !h.dead).length >= 5 },
  { id: "manuka", icon: "✨", name: "Manuka Keeper", desc: "Own a Manuka hive", reward: 8000, check: (s) => s.hives.some((h) => h.good === 4 && !h.dead) },
  { id: "royal", icon: "👑", name: "Royal Beekeeper", desc: "Own a Royal Jelly hive", reward: 25000, check: (s) => s.hives.some((h) => h.good === 5 && !h.dead) },
  { id: "survivor", icon: "🩹", name: "Survivor", desc: "Cure a hive disaster", reward: 4000, check: (s) => (s.stats.cured || 0) >= 1 },
  { id: "crafter", icon: "🔨", name: "Craftsbee", desc: "Craft 20 items", reward: 6000, check: (s) => (s.stats.crafted || 0) >= 20 },
  { id: "baker", icon: "🔥", name: "Baker", desc: "Bake in the oven", reward: 5000, check: (s) => (s.stats.baked || 0) >= 1 },
  { id: "globetrotter", icon: "🗺️", name: "Globetrotter", desc: "Visit all 15 markets", reward: 12000, check: (s) => (s.visited || []).length >= MARKETS.length },
  { id: "rising", icon: "⭐", name: "Rising Tycoon", desc: "Reach Level 20", reward: 30000, check: (s) => s.level >= 20 },
  { id: "millionaire", icon: "🏆", name: "Millionaire", desc: "Reach $1M net worth", reward: 100000, check: (s) => netWorth(s) >= 1_000_000 },
  // ── trading ──
  { id: "sales_10", icon: "🪙", name: "Regular Trader", desc: "Sell 10 goods", reward: 1500, check: (s) => (s.stats.sales || 0) >= 10 },
  { id: "sales_50", icon: "💵", name: "Seasoned Merchant", desc: "Sell 50 goods", reward: 6000, check: (s) => (s.stats.sales || 0) >= 50 },
  { id: "sales_100", icon: "💸", name: "Market Maven", desc: "Sell 100 goods", reward: 12000, check: (s) => (s.stats.sales || 0) >= 100 },
  { id: "sales_500", icon: "📊", name: "Trade Baron", desc: "Sell 500 goods", reward: 60000, check: (s) => (s.stats.sales || 0) >= 500 },
  { id: "sales_1000", icon: "📈", name: "Merchant Legend", desc: "Sell 1,000 goods", reward: 150000, check: (s) => (s.stats.sales || 0) >= 1000 },
  // ── wealth ──
  { id: "nw_100k", icon: "💼", name: "Well-Off", desc: "Reach $100k net worth", reward: 15000, check: (s) => netWorth(s) >= 100000 },
  { id: "nw_500k", icon: "💰", name: "Comfortable", desc: "Reach $500k net worth", reward: 40000, check: (s) => netWorth(s) >= 500000 },
  { id: "nw_2m", icon: "🤑", name: "Multi-Millionaire", desc: "Reach $2M net worth", reward: 200000, check: (s) => netWorth(s) >= 2000000 },
  { id: "nw_5m", icon: "💎", name: "Honey Magnate", desc: "Reach $5M net worth", reward: 400000, check: (s) => netWorth(s) >= 5000000 },
  { id: "nw_10m", icon: "🏰", name: "Honey Emperor", desc: "Reach $10M net worth", reward: 800000, check: (s) => netWorth(s) >= 10000000 },
  // ── levels ──
  { id: "lvl_10", icon: "🌟", name: "Established", desc: "Reach Level 10", reward: 6000, check: (s) => s.level >= 10 },
  { id: "lvl_15", icon: "✨", name: "Prospering", desc: "Reach Level 15", reward: 12000, check: (s) => s.level >= 15 },
  { id: "lvl_30", icon: "🎖️", name: "Big League", desc: "Reach Level 30", reward: 45000, check: (s) => s.level >= 30 },
  { id: "lvl_40", icon: "🏅", name: "Honey Tycoon", desc: "Reach Level 40", reward: 70000, check: (s) => s.level >= 40 },
  { id: "lvl_50", icon: "👑", name: "Market Master", desc: "Reach Level 50", reward: 120000, check: (s) => s.level >= 50 },
  { id: "lvl_75", icon: "💫", name: "Beekeeping Icon", desc: "Reach Level 75", reward: 250000, check: (s) => s.level >= 75 },
  { id: "lvl_100", icon: "🌈", name: "Grand Apiarist", desc: "Reach Level 100", reward: 500000, check: (s) => s.level >= 100 },
  // ── longevity ──
  { id: "day_30", icon: "📅", name: "One Month In", desc: "Reach day 30", reward: 5000, check: (s) => s.day >= 30 },
  { id: "day_100", icon: "🗓️", name: "Centennial", desc: "Reach day 100", reward: 20000, check: (s) => s.day >= 100 },
  { id: "day_365", icon: "🎉", name: "A Full Year", desc: "Reach day 365", reward: 80000, check: (s) => s.day >= 365 },
  // ── apiary ──
  { id: "hives_3", icon: "🐝", name: "Growing Apiary", desc: "Own 3 hives", reward: 2000, check: (s) => s.hives.filter((h) => !h.dead).length >= 3 },
  { id: "hives_8", icon: "🐝", name: "Bustling Apiary", desc: "Own 8 hives", reward: 12000, check: (s) => s.hives.filter((h) => !h.dead).length >= 8 },
  { id: "hives_12", icon: "🍯", name: "Grand Apiary", desc: "Own 12 hives", reward: 30000, check: (s) => s.hives.filter((h) => !h.dead).length >= 12 },
  { id: "hives_24", icon: "🎪", name: "Maxed Apiary", desc: "Own 24 hives", reward: 100000, check: (s) => s.hives.filter((h) => !h.dead).length >= 24 },
  { id: "all_honey", icon: "🌼", name: "Honey Sampler", desc: "Own hives for all 4 common honeys", reward: 15000, check: (s) => [0, 1, 2, 3].every((g) => s.hives.some((h) => h.good === g && !h.dead)) },
  { id: "all_hives", icon: "🌺", name: "Full Collection", desc: "Own one of every hive type", reward: 60000, check: (s) => [0, 1, 2, 3, 4, 5].every((g) => s.hives.some((h) => h.good === g && !h.dead)) },
  // ── crafting & baking ──
  { id: "craft_5", icon: "🧵", name: "First Batch", desc: "Craft 5 items", reward: 2000, check: (s) => (s.stats.crafted || 0) >= 5 },
  { id: "craft_50", icon: "🪡", name: "Artisan", desc: "Craft 50 items", reward: 10000, check: (s) => (s.stats.crafted || 0) >= 50 },
  { id: "craft_100", icon: "🎨", name: "Master Artisan", desc: "Craft 100 items", reward: 25000, check: (s) => (s.stats.crafted || 0) >= 100 },
  { id: "craft_500", icon: "🏛️", name: "Craft Guild", desc: "Craft 500 items", reward: 120000, check: (s) => (s.stats.crafted || 0) >= 500 },
  { id: "bake_5", icon: "🍰", name: "Home Baker", desc: "Bake 5 times", reward: 4000, check: (s) => (s.stats.baked || 0) >= 5 },
  { id: "bake_25", icon: "🧁", name: "Pastry Chef", desc: "Bake 25 times", reward: 20000, check: (s) => (s.stats.baked || 0) >= 25 },
  { id: "bake_100", icon: "🎂", name: "Bakery Empire", desc: "Bake 100 times", reward: 80000, check: (s) => (s.stats.baked || 0) >= 100 },
  // ── disasters & queens ──
  { id: "cure_5", icon: "💊", name: "Hive Medic", desc: "Cure 5 disasters", reward: 15000, check: (s) => (s.stats.cured || 0) >= 5 },
  { id: "cure_25", icon: "⚕️", name: "Disaster Expert", desc: "Cure 25 disasters", reward: 60000, check: (s) => (s.stats.cured || 0) >= 25 },
  { id: "good_queen", icon: "👸", name: "Prized Queen", desc: "Own a hive with a 150%+ producer queen", reward: 8000, check: (s) => s.hives.some((h) => !h.dead && (h.queen ? h.queen.prod : 1) >= 1.5) },
  { id: "royal_lineage", icon: "💠", name: "Royal Lineage", desc: "Own a 140%-honey, 120%-resistant queen", reward: 25000, check: (s) => s.hives.some((h) => !h.dead && (h.queen ? h.queen.prod : 1) >= 1.4 && (h.queen ? h.queen.resist : 1) >= 1.2) },
  // ── protection & bank ──
  { id: "insured_1", icon: "🛡️", name: "Insured", desc: "Insure a hive", reward: 3000, check: (s) => s.hives.some((h) => h.insured) },
  { id: "insured_3", icon: "🔰", name: "Well-Protected", desc: "Insure 3 hives", reward: 12000, check: (s) => s.hives.filter((h) => h.insured).length >= 3 },
  { id: "loan_taken", icon: "🏧", name: "In the Red", desc: "Take out a bank loan", reward: 2000, check: (s) => (s.loan || 0) > 0 },
  { id: "orders_3", icon: "🤖", name: "Automated", desc: "Set 3 auto-sell orders", reward: 8000, check: (s) => (s.orders || []).length >= 3 },
  { id: "auto_active", icon: "⚙️", name: "Hands Off", desc: "Have auto-tend running", reward: 5000, check: (s) => (s.autoDays || 0) > 0 },
  // ── legacy & devotion ──
  { id: "prestige_1", icon: "🌱", name: "New Beginnings", desc: "Retire once", reward: 20000, check: (s) => (s.prestige || 0) >= 1 },
  { id: "prestige_3", icon: "♻️", name: "Serial Retiree", desc: "Retire 3 times", reward: 80000, check: (s) => (s.prestige || 0) >= 3 },
  { id: "prestige_5", icon: "🧬", name: "Legacy Builder", desc: "Retire 5 times", reward: 200000, check: (s) => (s.prestige || 0) >= 5 },
  { id: "streak_7", icon: "🔥", name: "Devoted", desc: "Reach a 7-day login streak", reward: 10000, check: (s) => (s.streak || 0) >= 7 },
  { id: "streak_30", icon: "☀️", name: "Unwavering", desc: "Reach a 30-day login streak", reward: 50000, check: (s) => (s.streak || 0) >= 30 },
  // ── stockpile ──
  { id: "pantry_100", icon: "📦", name: "Stocked Up", desc: "Hold 100 items in the pantry", reward: 4000, check: (s) => totalItems(s) >= 100 },
  { id: "pantry_500", icon: "🏬", name: "Warehouse", desc: "Hold 500 items in the pantry", reward: 25000, check: (s) => totalItems(s) >= 500 },
  { id: "hoard_royal", icon: "🍯", name: "Jelly Hoard", desc: "Hold 20 Royal Jelly at once", reward: 30000, check: (s) => (s.inv[5] || 0) >= 20 },
];

// Mutates `s`: awards any newly-earned achievements (adds cash + id). Returns them.
export function earnAchievements(s) {
  if (!s.ach) s.ach = [];
  const got = [];
  for (const a of ACHIEVEMENTS) {
    if (!s.ach.includes(a.id) && a.check(s)) {
      s.ach.push(a.id);
      s.cash += a.reward;
      got.push(a);
    }
  }
  return got;
}

// Daily login streak (real-world days). Returns { streak, reward } if newly claimed.
export function claimDaily(s, today) {
  if (!today || s.streakDay === today) return null;
  const prev = s.streakDay ? new Date(s.streakDay + "T00:00:00") : null;
  const now = new Date(today + "T00:00:00");
  const consecutive = prev && Math.round((now - prev) / 86400000) === 1;
  const streak = consecutive ? (s.streak || 0) + 1 : 1;
  const reward = 500 + 250 * Math.min(streak - 1, 10); // grows to $3,000 at a 10-day streak
  s.streak = streak;
  s.streakDay = today;
  s.cash += reward;
  return { streak, reward };
}

/* ── Bank (loans), standing orders, offline production & requeening ──────────── */

// Loans — borrow against a credit line, repay any time; interest accrues daily.
export const LOAN_RATE = 0.03; // 3% per day
export function creditLimit(s) {
  return 25000 + Math.min(100, s.level | 0) * 15000 + (s.prestige || 0) * 40000;
}
export function takeLoan(s, amount) {
  const room = Math.max(0, creditLimit(s) - (s.loan || 0));
  const n = Math.min(Math.max(0, Math.floor(amount)), room);
  if (n < 1) return { state: s, ok: false, note: room < 1 ? "You're at your credit limit" : "Enter an amount" };
  const st = clone(s);
  st.cash += n;
  st.loan = (st.loan || 0) + n;
  return { state: st, ok: true, note: `Borrowed ${money(n)} — you owe ${money(st.loan)}` };
}
export function repayLoan(s, amount) {
  const pay = Math.min(Math.max(0, Math.floor(amount)), s.loan || 0, s.cash);
  if (pay < 1) return { state: s, ok: false, note: s.loan > 0 ? "Not enough cash" : "No debt to repay" };
  const st = clone(s);
  st.cash -= pay;
  st.loan = (st.loan || 0) - pay;
  return { state: st, ok: true, note: st.loan > 0 ? `Repaid ${money(pay)} — ${money(st.loan)} left` : `Loan cleared! (−${money(pay)})` };
}

// Standing auto-sell orders — sell a good automatically each day the price clears a floor.
export const MAX_ORDERS = 8;
export function addOrder(s, good, minPrice) {
  if ((s.orders || []).length >= MAX_ORDERS) return { state: s, ok: false, note: `Up to ${MAX_ORDERS} orders` };
  const mp = Math.max(1, Math.floor(minPrice) || 0);
  const st = clone(s);
  st.orders = (st.orders || []).filter((o) => o.good !== good); // one order per good
  st.orders.push({ good, minPrice: mp });
  return { state: st, ok: true, note: `Auto-sell ${GOODS[good].name} at ${money(mp)}+` };
}
export function removeOrder(s, good) {
  const st = clone(s);
  st.orders = (st.orders || []).filter((o) => o.good !== good);
  return { state: st, ok: true };
}

// Offline production — grant idle honey for real time away (1 game-day per hour, capped).
export const OFFLINE_HOURS_PER_DAY = 1;
export const OFFLINE_MAX_DAYS = 24;
export function applyOffline(s, elapsedMs) {
  const days = Math.min(OFFLINE_MAX_DAYS, Math.floor((elapsedMs || 0) / (OFFLINE_HOURS_PER_DAY * 3600000)));
  if (days < 1) return null;
  let items = 0;
  let room = Math.max(0, s.cap - totalItems(s));
  for (const h of s.hives) {
    if (h.disaster || h.dead || room <= 0) continue;
    const per = Math.max(1, Math.round(h.perTend * queenProd(h)));
    const produced = Math.floor(days / (h.honeyEvery || 1)) * per;
    const take = Math.min(produced, room);
    if (take > 0) {
      s.inv[h.good] += take;
      room -= take;
      items += take;
    }
  }
  return items > 0 ? { days, items } : null;
}

// Requeen — pay to install a fresh random queen (a gamble for better traits).
export const requeenCost = (h) => Math.round(hiveValue(h) * 0.15);
export function requeen(s, id) {
  const h = s.hives.find((x) => x.id === id);
  if (!h || h.dead) return { state: s, ok: false };
  const cost = requeenCost(h);
  if (s.cash < cost) return { state: s, ok: false, note: `Need ${money(cost)} to requeen ${h.name}` };
  const st = clone(s);
  const hh = st.hives.find((x) => x.id === id);
  st.cash -= cost;
  hh.queen = makeQueen();
  return { state: st, ok: true, note: `New queen for ${hh.name} — ${Math.round(hh.queen.prod * 100)}% honey, ${Math.round(hh.queen.resist * 100)}% resistance` };
}
